import { Component, NgZone, OnChanges, OnDestroy, output, SimpleChanges, input, signal } from '@angular/core';
import {
  GroupedResourceListItem,
  IEventInfoResourceResponseBm,
  IPropertyFilter,
  IReservationDetail,
  IResourceFilterUpdateCrate,
  IResourceListItem,
  ResourceReservationStatusType,
  ResourceUnavailabilityReason,
  TouchOneHttpService,
} from '@outlook-addin/cue-http';
import { Store } from '@ngrx/store';
import * as fromModule from '../ngrx/reducers';
import { ReservationActions, ResourcesActions } from '../ngrx/actions';
import { NavigationDetailInfo } from '@cue/navigation-services';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  cueAction,
  cueArrowRight,
  cueClose,
  cueExternalLink,
  cueInfo,
  cueMore,
  cueRefresh,
  cueSearch,
  cueArrowDown,
  IconComponent,
} from '@cue/ui/icons';
import {
  AddinConfigService,
  getResourceAvailabilityRestrictionsMode,
  ResourceAvailabilityRestrictionsModeId,
} from '@outlook-addin/shared';
import { translation } from '@cue/translations';
import AppointmentCompose = Office.AppointmentCompose;
import EmailUser = Office.EmailUser;

import LocationDetails = Office.LocationDetails;
import { TextboxComponent } from '@cue/assist/ui/inputs';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ResourceSummaryDetailComponent } from '../resource-summary-detail/resource-summary-detail.component';
import { AssignToPipe, ResourceAlphabeticallyPipe } from '../shared/pipes';
import { ResourceAvailabilityComponent } from '../resource-availability/resource-availability.component';
import { LocalizedStringPipe } from '@cue/utilities';
import { localized } from '@cue/translate';
import { LocalizedString, ResourceAssignType } from '@cue/api';
import { Pack } from '@cue/data';
import { AsyncPipe, NgStyle } from '@angular/common';

export type ResourceNotification = {
  id: number;
  messageKey: string;
  resources: string;
  resourceDetails: ResourceNotificationResource[];
  start: Date;
  end: Date;
  confirmation?: ResourceNotificationConfirmation;
};

export type ResourceNotificationResource = {
  displayName: string;
  resource: IResourceListItem;
};

export type ResourceNotificationConfirmation = {
  primaryAction: () => void;
  secondaryAction: () => void;
  primaryLabelKey?: string;
  secondaryLabelKey?: string;
  timeoutMs: number;
  defaultAction?: 'primary' | 'secondary';
};

export type ResourceSelectionWarning = {
  messageKey: string;
  resourceName: string;
  resource: IResourceListItem;
  start: Date;
  end: Date;
  confirmation?: ResourceNotificationConfirmation;
};

type CurrentEventIdentity = {
  iCalUid: string | null;
  eventId: string | null;
  start: Date | null;
  end: Date | null;
  startLocalDateTime: string | null;
  endLocalDateTime: string | null;
  startTimeZone: string | null;
  endTimeZone: string | null;
};

type CurrentOutlookReservationSnapshot = {
  locations: LocationDetails[];
  recipients: EmailUser[];
  start: Date | null;
  end: Date | null;
};

type EventInfoAttendeeStatus = {
  response: string;
  time: string;
};

@Component({
  selector: 'addin-resource-list',
  templateUrl: './resource-list.component.html',
  styleUrls: ['./resource-list.component.scss'],
  imports: [
    TextboxComponent,
    LoadingComponent,
    TranslocoPipe,
    IconComponent,
    ResourceAvailabilityComponent,
    AssignToPipe,
    ResourceSummaryDetailComponent,
    ReactiveFormsModule,
    LocalizedStringPipe,
    ResourceAlphabeticallyPipe,
    NgStyle,
    AsyncPipe,
  ],
})
export class ResourceListComponent implements OnChanges, OnDestroy {
  ICONS = {
    cueClose,
    cueAction,
    cueSearch,
    cueArrowRight,
    cueExternalLink,
    cueRefresh,
    cueMore,
    cueArrowDown,
    cueInfo,
  };
  protected readonly translation = translation;
  resourceAssignType = ResourceAssignType;
  timelineDetailItemId: number;
  timelineDetailTopOffset = signal(0);

  search = new FormControl<string>('', { nonNullable: true });

  selectedOutlookLocations = signal<LocationDetails[]>([]);
  selectedOutlookRecipientEmails = signal<string[]>([]);
  selectedResourceIdList = signal<number[]>([]);
  filterUpdate: IResourceFilterUpdateCrate = {
    showOnlyAvailable: false,
    resourceName: null,
    additionalFilterList: [],
  };

  resourceDetail$: Observable<Pack<NavigationDetailInfo>>;

  selectedResourceDetail: IResourceListItem;

  protected canNavigateToAssist: boolean;
  resourceFilter = input<IResourceFilterUpdateCrate>();
  resourceFilters = input<IPropertyFilter[]>([]);
  groupedResourceList = input<Pack<GroupedResourceListItem[]>>();
  resourceList = input<Pack<IResourceListItem[]>>();
  eventStart = signal<any>(null);
  eventEnd = signal<any>(null);

  refresh = output<void>();
  resourceSelectionWarningChange = output<ResourceSelectionWarning | null>();
  resourceNotification = output<ResourceNotification>();
  private readonly resourceAvailabilityRestrictionsMode = getResourceAvailabilityRestrictionsMode(
    this.configService.value.configuration
  );
  private readonly shouldUseResourceAvailabilityRestrictions =
    this.resourceAvailabilityRestrictionsMode !== ResourceAvailabilityRestrictionsModeId.DoNotModify;
  private readonly shouldAskBeforeModifyingOutlookEvent =
    this.resourceAvailabilityRestrictionsMode === ResourceAvailabilityRestrictionsModeId.AskBeforeModifying;
  private readonly shouldAutomaticallyEnforceResourceAvailabilityRestrictions =
    this.resourceAvailabilityRestrictionsMode === ResourceAvailabilityRestrictionsModeId.AutomaticallyEnforce;
  private readonly resourceRestrictionConfirmationTimeoutMs = 20000;
  private readonly handleItemChangeBound = this.handleItemChange.bind(this);
  private readonly handleLocationChangeBound = this.handleLocationChange.bind(this);
  private readonly handleRecipientsChangeBound = this.handleRecipientsChange.bind(this);
  private readonly removingResourceIds = new Set<number>();
  private readonly pendingResourceRestrictionConfirmationKeys = new Set<string>();
  private readonly confirmedResourceRestrictionOverrideKeys = new Set<string>();
  private validateSelectedResourcesAfterResourceListReload = false;
  private outlookStateInitialized = false;
  private currentEventItemId: string | null = null;
  private currentEventId: string | null = null;
  private currentEventStart: Date | null = null;
  private currentEventEnd: Date | null = null;
  private currentEventStartLocalDateTime: string | null = null;
  private currentEventEndLocalDateTime: string | null = null;
  private currentEventStartTimeZone: string | null = null;
  private currentEventEndTimeZone: string | null = null;
  private currentReservationDetail: IReservationDetail | null = null;
  private currentEventDetail: any | null = null;
  private reloadReservationDetailTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private dismissedResourceRemovalConfirmationResourceIds = new Set<number>();
  private readonly subscriptionList = new Subscription();
  private notificationId = 0;

  constructor(
    private store: Store<fromModule.State>,
    private configService: AddinConfigService,
    private touchOneHttpService: TouchOneHttpService,
    private zone: NgZone,
    private translocoService: TranslocoService
  ) {
    this.canNavigateToAssist = !this.configService.value.configuration.isCAFromAddin;
    this.resourceDetail$ = this.store.select(fromModule.selectResourceDetail);
    this.subscriptionList.add(
      this.search.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((x) => {
        this.searchTextChanged(x);
      })
    );
    this.subscriptionList.add(
      this.store.select(fromModule.selectReservationDetail).subscribe((pack) => {
        this.currentReservationDetail = pack.data ?? null;
      })
    );
    this.subscriptionList.add(
      this.store.select(fromModule.selectEventDetail).subscribe((eventDetail) => {
        this.currentEventDetail = eventDetail ?? null;
      })
    );
    this.subscriptionList.add(
      this.store.select(fromModule.selectDismissedResourceRemovalConfirmationResourceIds).subscribe((resourceIds) => {
        this.dismissedResourceRemovalConfirmationResourceIds = new Set(resourceIds);
      })
    );

    if (Office.context.mailbox) {
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.AppointmentTimeChanged,

        this.handleItemChangeBound
      );
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.EnhancedLocationsChanged,
        this.handleLocationChangeBound
      );
      Office.context.mailbox.item.addHandlerAsync(Office.EventType.RecipientsChanged, this.handleRecipientsChangeBound);
      const itemData = Office.context.mailbox.item as AppointmentCompose;
      this.performAppointmentAction(itemData);
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
    if (this.reloadReservationDetailTimeoutId) {
      clearTimeout(this.reloadReservationDetailTimeoutId);
    }

    if (!Office.context.mailbox) {
      return;
    }

    Office.context.mailbox.item.removeHandlerAsync(Office.EventType.AppointmentTimeChanged, this.handleItemChangeBound);
    Office.context.mailbox.item.removeHandlerAsync(
      Office.EventType.EnhancedLocationsChanged,
      this.handleLocationChangeBound
    );
    Office.context.mailbox.item.removeHandlerAsync(
      Office.EventType.RecipientsChanged,
      this.handleRecipientsChangeBound
    );
  }

  changeCurrentOpenedTimelineDetail(id: number | null, event: MouseEvent) {
    if (id === null) {
      this.timelineDetailItemId = null;
      this.timelineDetailTopOffset.set(0);
      event.stopPropagation();
      return;
    }

    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) {
      event.stopPropagation();
      return;
    }

    this.timelineDetailTopOffset.set(this.getTimelineDetailTopOffset(target));
    this.timelineDetailItemId = id;
    requestAnimationFrame(() => this.updateTimelineDetailTopOffset(id, target));
    event.stopPropagation();
  }

  private updateTimelineDetailTopOffset(id: number, target: HTMLElement) {
    if (this.timelineDetailItemId !== id) {
      return;
    }

    this.timelineDetailTopOffset.set(this.getTimelineDetailTopOffset(target));
  }

  private getTimelineDetailTopOffset(target: HTMLElement): number {
    const resourceItem = target.closest<HTMLElement>('.resource-item');
    const resourcesList = target.closest<HTMLElement>('.resources-list');
    const accordionHeader = target.closest<HTMLElement>('.accordion')?.querySelector<HTMLElement>('.accordion-header');

    if (!resourceItem || !resourcesList) {
      return 0;
    }

    const resourceItemRect = resourceItem.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const resourcesListRect = resourcesList.getBoundingClientRect();
    const upperBoundary = Math.max(
      resourcesListRect.top,
      accordionHeader?.getBoundingClientRect().bottom ?? resourcesListRect.top
    );
    const tooltip = resourceItem.querySelector<HTMLElement>('addin-resource-availability');
    const tooltipHeight = tooltip?.getBoundingClientRect().height ?? 110;
    const gap = 4;
    const minTop = upperBoundary + gap;
    const maxTop = Math.max(minTop, resourcesListRect.bottom - gap - tooltipHeight);
    const preferredTop = targetRect.top - gap - tooltipHeight;
    const clampedTop = Math.min(Math.max(preferredTop, minTop), maxTop);

    return clampedTop - resourceItemRect.top;
  }

  handleLocationChange(eventArgs: any) {
    if (!Array.isArray(eventArgs?.enhancedLocations)) {
      this.refreshSelectedLocationsFromOutlook(true);
      return;
    }

    this.zone.run(() => {
      this.syncSelectedResourcesFromOutlookLocations(eventArgs.enhancedLocations, true);
    });
  }

  handleRecipientsChange() {
    this.refreshSelectedRecipientsFromOutlook(true);
  }

  handleItemChange(eventArgs: any) {
    this.zone.run(() => {
      const shouldValidateResources =
        this.outlookStateInitialized && this.hasEventTimeChanged(eventArgs.start, eventArgs.end);
      if (shouldValidateResources) {
        this.clearDismissedResourceRemovalConfirmations();
      }

      this.updateResourceFilter(
        eventArgs.start,
        eventArgs.end,
        this.filterUpdate.iCalUid,
        this.getOutlookResourceEmails()
      );

      if (shouldValidateResources) {
        const handledPastEventEnd =
          this.shouldUseResourceAvailabilityRestrictions &&
          this.isEventEndInPast(eventArgs.end) &&
          this.validateSelectedResourcesAgainstPastEventEnd();

        this.validateSelectedResourcesAfterResourceListReload = !handledPastEventEnd;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const resourceList = this.resourceList();
    if (changes['resourceList'] && resourceList && !resourceList.loading && resourceList.data?.length > 0) {
      if (this.selectedOutlookLocations().length > 0 || this.selectedOutlookRecipientEmails().length > 0) {
        this.syncSelectedResourcesFromCurrentOutlookState();
        if (this.syncCurrentEventResourceEmailsFromSelectedResources()) {
          return;
        }

        if (this.shouldValidateSelectedResourcesAfterResourceListReload()) {
          this.validateSelectedResourcesAfterResourceListReload = false;
          this.validateSelectedResourcesAgainstCurrentList();
        }
      } else if (this.validateSelectedResourcesAfterResourceListReload) {
        this.validateSelectedResourcesAfterResourceListReload = false;
      }
    }

    if (changes['resourceFilter'] && this.resourceFilter()) {
      this.filterUpdate = {
        ...this.filterUpdate,
        resourceName: this.resourceFilter().resourceName ?? '',
        showOnlyAvailable:
          this.resourceFilter().showOnlyAvailable != null ? this.resourceFilter().showOnlyAvailable : false,
      };
      this.search.setValue(this.filterUpdate.resourceName);
    }
  }

  showOnlyAvailable() {
    this.filterUpdate = {
      ...this.filterUpdate,
      showOnlyAvailable: !this.filterUpdate.showOnlyAvailable,
    };
    this.store.dispatch(
      ResourcesActions.updateOnlyAvailableFilter({
        onlyAvailable: this.filterUpdate.showOnlyAvailable,
      })
    );
    localStorage.setItem('showOnlyAvailableFilter', this.filterUpdate.showOnlyAvailable === true ? 'true' : 'false');
  }

  private searchTextChanged(data: string) {
    this.filterUpdate = {
      ...this.filterUpdate,
      resourceName: data,
    };
    this.store.dispatch(
      ResourcesActions.searchTextChanged({
        searchText: this.filterUpdate.resourceName,
      })
    );
  }

  selectDeselectResource(resourceId: number, event: MouseEvent, assignTo: ResourceAssignType) {
    if (!assignTo || assignTo === ResourceAssignType.Restricted) {
      return;
    }
    this.selectDeselectResourceFromEvent(resourceId, event);
    event.stopPropagation();
  }

  selectDeselectResourceFromDetail(resourceId: number) {
    if (
      this.selectDeselectResourceFromEvent(resourceId, undefined, () => {
        this.selectedResourceDetail = null;
      })
    ) {
      this.selectedResourceDetail = null;
    }
  }

  private selectDeselectResourceFromEvent(resourceId: number, event?: MouseEvent, successAction?: () => void): boolean {
    const resource = this.getResourceById(resourceId);
    const attendee = this.getResourceListItemById(resourceId);
    if (!resource || !attendee) {
      return false;
    }

    const shouldBeSelected = !this.isResourceSelected(resourceId);
    const resourceForValidation = this.getResourceWithCurrentEventUnavailabilityReason(resource);
    const applyResourceSelection = (confirmedResourceRestrictionOverride = false) => {
      if (confirmedResourceRestrictionOverride) {
        this.addConfirmedResourceRestrictionOverride(resourceForValidation);
      }

      this.resourceSelectionWarningChange.emit(null);
      this.selectDeselectOutlookAttendees(attendee, shouldBeSelected, (success) => {
        if (!success && confirmedResourceRestrictionOverride) {
          this.removeConfirmedResourceRestrictionOverride(resourceForValidation);
        }

        if (success && shouldBeSelected) {
          this.store.dispatch(
            ReservationActions.showReservationDetailResources({
              resourceIds: [resourceId],
            })
          );
          this.scheduleReservationDetailReloadFromCurrentOutlookState();
          successAction?.();
        }

        if (success && !shouldBeSelected) {
          this.removeConfirmedResourceRestrictionOverride(resourceForValidation);
        }
      });
    };

    if (
      shouldBeSelected &&
      this.shouldUseResourceAvailabilityRestrictions &&
      this.isResourceUnavailableByResourceType(resourceForValidation)
    ) {
      this.showResourceSelectionWarning(resourceForValidation, event, () => applyResourceSelection(true), {
        useQuestionMessage: false,
      });
      return false;
    }

    if (
      shouldBeSelected &&
      this.shouldUseResourceAvailabilityRestrictions &&
      !this.isResourceAvailable(resourceForValidation)
    ) {
      this.showResourceSelectionWarning(resourceForValidation, event, () => applyResourceSelection(true));
      return false;
    }

    applyResourceSelection();
    return true;
  }

  isResourceSelected(resourceId: number) {
    return this.selectedResourceIdList().indexOf(resourceId) > -1;
  }

  showResourceDetail(resourceId: number | null, event: MouseEvent) {
    this.resourceSelectionWarningChange.emit(null);
    if (!resourceId) {
      this.selectedResourceDetail = null;
    } else {
      this.selectedResourceDetail = this.resourceList().data.filter((x) => x.resourceId === resourceId)[0];
      this.store.dispatch(ResourcesActions.loadResourceDetail({ resourceId: this.selectedResourceDetail.resourceId }));
    }
    event.stopPropagation();
  }

  private showResourceSelectionWarning(
    resource: IResourceListItem,
    event?: MouseEvent,
    confirmedSelectionAction?: () => void,
    options: {
      useQuestionMessage?: boolean;
    } = {}
  ) {
    const shouldOfferConfirmation = this.shouldAskBeforeModifyingOutlookEvent;
    const useQuestionMessage = options.useQuestionMessage ?? true;
    const useResourceTypeConfirmationLabels = this.isResourceUnavailableByResourceType(resource);

    this.selectedResourceDetail = null;
    this.resourceSelectionWarningChange.emit({
      messageKey: this.getResourceSelectionWarningMessageKey(resource, shouldOfferConfirmation && useQuestionMessage),
      resourceName: this.getResourceDisplayName(resource),
      resource,
      start: this.getWarningDateValue(this.resourceFilter()?.start ?? this.eventStart()),
      end: this.getWarningDateValue(this.resourceFilter()?.end ?? this.eventEnd()),
      confirmation: shouldOfferConfirmation
        ? {
            primaryAction: confirmedSelectionAction ?? (() => undefined),
            secondaryAction: () => undefined,
            primaryLabelKey: useResourceTypeConfirmationLabels
              ? this.translation.outlookAddin.confirmationKeep
              : undefined,
            secondaryLabelKey: useResourceTypeConfirmationLabels
              ? this.translation.outlookAddin.confirmationRemove
              : undefined,
            timeoutMs: this.resourceRestrictionConfirmationTimeoutMs,
            defaultAction: 'secondary',
          }
        : undefined,
    });
    event?.stopPropagation();
  }

  private getResourceSelectionWarningMessageKey(resource: IResourceListItem, useQuestionMessage: boolean): string {
    const isUnavailableByResourceType = this.isResourceUnavailableByResourceType(resource);

    if (useQuestionMessage) {
      return isUnavailableByResourceType
        ? this.translation.outlookAddin.resourceCannotBeSelectedDueToOccupiedResourceTypeQuestion
        : this.translation.outlookAddin.resourceCannotBeSelectedQuestion;
    }

    return isUnavailableByResourceType
      ? this.translation.outlookAddin.resourceCannotBeSelectedDueToOccupiedResourceType
      : this.translation.outlookAddin.resourceCannotBeSelected;
  }

  private getWarningDateValue(value: unknown): Date {
    return value instanceof Date ? value : new Date(value as string);
  }

  private hasEventTimeChanged(start: unknown, end: unknown): boolean {
    return (
      !!start &&
      !!end &&
      !!this.eventStart() &&
      !!this.eventEnd() &&
      (!this.hasSameDateValue(this.eventStart(), start) || !this.hasSameDateValue(this.eventEnd(), end))
    );
  }

  private hasSameDateValue(first: unknown, second: unknown): boolean {
    const firstTime = new Date(first as string).getTime();
    const secondTime = new Date(second as string).getTime();

    return Number.isFinite(firstTime) && Number.isFinite(secondTime) && firstTime === secondTime;
  }

  private shouldValidateSelectedResourcesAfterResourceListReload(): boolean {
    return this.validateSelectedResourcesAfterResourceListReload || this.hasEventTimeMovedFromOriginalEvent();
  }

  private hasEventTimeMovedFromOriginalEvent(): boolean {
    const start = this.filterUpdate.start ?? this.eventStart();
    const end = this.filterUpdate.end ?? this.eventEnd();

    return (
      this.outlookStateInitialized &&
      !!start &&
      !!end &&
      !!this.currentEventStart &&
      !!this.currentEventEnd &&
      (!this.hasSameDateValue(this.currentEventStart, start) || !this.hasSameDateValue(this.currentEventEnd, end))
    );
  }

  navigateToResourceDetailInAssist(resourceGuid: string, event: MouseEvent) {
    window.open(
      this.configService.value.configuration.assistURL + '/dashboard/resource-info?id=' + resourceGuid,
      '_blank'
    );
    event.stopPropagation();
  }

  private updateResourceFilter(
    start?: string | Date,
    end?: string | Date,
    iCalUid?: string | null,
    currentEventResourceEmails = this.getOutlookResourceEmails(),
    silentResourceListReload = false
  ) {
    this.eventStart.set(start);
    this.eventEnd.set(end);
    this.filterUpdate = {
      ...this.filterUpdate,
      iCalUid: iCalUid !== undefined ? iCalUid : (this.filterUpdate.iCalUid ?? null),
      currentEventId: this.currentEventId,
      currentEventResourceEmails,
      currentEventStartLocalDateTime: this.currentEventStartLocalDateTime,
      currentEventEndLocalDateTime: this.currentEventEndLocalDateTime,
      currentEventStartTimeZone: this.currentEventStartTimeZone,
      currentEventEndTimeZone: this.currentEventEndTimeZone,
    };
    this.store.dispatch(
      ResourcesActions.updateStartEndFilter({
        start: new Date(start),
        end: new Date(end),
        iCalUid: this.filterUpdate.iCalUid,
        currentEventId: this.currentEventId,
        currentEventResourceEmails,
        currentEventStart: this.currentEventStart,
        currentEventEnd: this.currentEventEnd,
        currentEventStartLocalDateTime: this.currentEventStartLocalDateTime,
        currentEventEndLocalDateTime: this.currentEventEndLocalDateTime,
        currentEventStartTimeZone: this.currentEventStartTimeZone,
        currentEventEndTimeZone: this.currentEventEndTimeZone,
        silentResourceListReload,
      })
    );
  }

  private selectDeselectOutlookAttendees(
    attendee: EmailUser,
    shouldBeSelected: boolean,
    callback?: (success: boolean) => void
  ) {
    Office.context.mailbox.item.enhancedLocation.getAsync((x) => {
      if (x.status !== Office.AsyncResultStatus.Succeeded) {
        if (!shouldBeSelected) {
          this.removeResourceRecipient(attendee.emailAddress, (success) => {
            this.refreshSelectedOutlookResources();
            callback?.(success);
          });
          return;
        }

        callback?.(false);
        return;
      }

      const currentAttendeeList: LocationDetails[] = x.value ?? [];
      const existingLocation = this.findLocationForAttendee(currentAttendeeList, attendee.emailAddress);

      if (shouldBeSelected) {
        if (existingLocation) {
          this.zone.run(() => this.syncSelectedResourcesFromOutlookLocations(currentAttendeeList));
          callback?.(true);
          return;
        }

        Office.context.mailbox.item.enhancedLocation.addAsync(
          [
            {
              id: attendee.emailAddress,
              type: Office.MailboxEnums.LocationType.Room,
            },
          ],
          (result) => {
            this.refreshSelectedOutlookResources();
            callback?.(result.status === Office.AsyncResultStatus.Succeeded);
          }
        );
        return;
      }

      this.removeResourceFromOutlookEvent(attendee.emailAddress, (success) => {
        this.refreshSelectedOutlookResources();
        callback?.(success);
      });
    });
  }

  private removeResourceFromOutlookEvent(resourceEmail: string, callback?: (success: boolean) => void) {
    this.removeResourceLocation(resourceEmail, (locationRemoved) => {
      this.removeResourceRecipient(resourceEmail, (recipientRemoved) =>
        callback?.(locationRemoved && recipientRemoved)
      );
    });
  }

  private removeResourceLocation(resourceEmail: string, callback: (success: boolean) => void) {
    Office.context.mailbox.item.enhancedLocation.getAsync((x) => {
      if (x.status !== Office.AsyncResultStatus.Succeeded) {
        callback(false);
        return;
      }

      const existingLocation = this.findLocationForAttendee(x.value ?? [], resourceEmail);
      if (!existingLocation) {
        callback(true);
        return;
      }

      Office.context.mailbox.item?.enhancedLocation.removeAsync([existingLocation.locationIdentifier], (result) =>
        callback(result.status === Office.AsyncResultStatus.Succeeded)
      );
    });
  }

  private removeResourceRecipient(resourceEmail: string, callback: (success: boolean) => void) {
    const item = Office.context.mailbox.item as AppointmentCompose;
    this.removeResourceRecipientFromList(item.requiredAttendees, resourceEmail, (requiredRemoved) => {
      this.removeResourceRecipientFromList(item.optionalAttendees, resourceEmail, (optionalRemoved) =>
        callback(requiredRemoved && optionalRemoved)
      );
    });
  }

  private removeResourceRecipientFromList(
    recipients: Office.Recipients,
    resourceEmail: string,
    callback: (success: boolean) => void
  ) {
    if (!recipients) {
      callback(true);
      return;
    }

    recipients.getAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        callback(false);
        return;
      }

      const resourceEmailKey = this.normalizeLocationKey(resourceEmail);
      const currentRecipients = result.value ?? [];
      const nextRecipients = currentRecipients.filter(
        (recipient) => this.normalizeLocationKey(recipient.emailAddress) !== resourceEmailKey
      );

      if (nextRecipients.length === currentRecipients.length) {
        callback(true);
        return;
      }

      recipients.setAsync(nextRecipients, (setResult) =>
        callback(setResult.status === Office.AsyncResultStatus.Succeeded)
      );
    });
  }

  private refreshSelectedLocationsFromOutlook(validateAddedResources = false) {
    Office.context.mailbox.item.enhancedLocation.getAsync((x) => {
      if (x.status !== Office.AsyncResultStatus.Succeeded) {
        return;
      }

      this.zone.run(() => {
        this.syncSelectedResourcesFromOutlookLocations(x.value ?? [], validateAddedResources);
      });
    });
  }

  private refreshSelectedRecipientsFromOutlook(validateAddedResources = false) {
    this.getOutlookRecipientEmails((emails) => {
      this.zone.run(() => {
        this.syncSelectedResourcesFromOutlookRecipients(emails, validateAddedResources);
      });
    });
  }

  private refreshSelectedOutlookResources() {
    this.refreshSelectedLocationsFromOutlook();
    this.refreshSelectedRecipientsFromOutlook();
  }

  private scheduleReservationDetailReloadFromCurrentOutlookState() {
    if (!this.getReservationDetailRequestSource()) {
      return;
    }

    if (this.reloadReservationDetailTimeoutId) {
      clearTimeout(this.reloadReservationDetailTimeoutId);
    }

    this.reloadReservationDetailTimeoutId = setTimeout(() => {
      this.reloadReservationDetailTimeoutId = null;
      this.reloadReservationDetailFromCurrentOutlookState();
    }, 100);
  }

  private reloadReservationDetailFromCurrentOutlookState() {
    const requestSource = this.getReservationDetailRequestSource();
    if (!requestSource) {
      return;
    }

    this.getCurrentOutlookReservationSnapshot((snapshot) => {
      if (!snapshot) {
        return;
      }

      this.zone.run(() => {
        this.store.dispatch(
          ReservationActions.loadReservationDetail({
            eventDetail: this.createReservationDetailRequest(requestSource, snapshot),
            preserveCurrentDetail: true,
            skipNavigation: true,
            preserveLocationChangeState: true,
          })
        );
      });
    });
  }

  private getReservationDetailRequestSource(): any | null {
    return this.currentReservationDetail ?? this.currentEventDetail;
  }

  private getCurrentOutlookReservationSnapshot(callback: (snapshot: CurrentOutlookReservationSnapshot | null) => void) {
    const item = Office.context.mailbox?.item as AppointmentCompose | undefined;
    if (!item?.enhancedLocation?.getAsync) {
      callback(null);
      return;
    }

    let pendingReads = 3;
    let callbackCalled = false;
    let currentLocations: LocationDetails[] | null = null;
    let currentRecipients: EmailUser[] | null = null;
    let currentStart: Date | null = null;
    let currentEnd: Date | null = null;

    const completeRead = () => {
      pendingReads--;
      if (pendingReads > 0 || callbackCalled) {
        return;
      }

      callbackCalled = true;
      callback({
        locations: currentLocations ?? [],
        recipients: currentRecipients ?? [],
        start: currentStart,
        end: currentEnd,
      });
    };

    item.enhancedLocation.getAsync((result) => {
      currentLocations = result.status === Office.AsyncResultStatus.Succeeded ? (result.value ?? []) : [];
      completeRead();
    });

    this.getOutlookRecipients((recipients) => {
      currentRecipients = recipients;
      completeRead();
    });

    this.getOutlookEventDates(item, (start, end) => {
      currentStart = start;
      currentEnd = end;
      completeRead();
    });
  }

  private createReservationDetailRequest(requestSource: any, snapshot: CurrentOutlookReservationSnapshot) {
    const locations = snapshot.locations.map((location) => this.createEventInfoLocation(location));
    const attendeeStatusByKey = this.createEventInfoAttendeeStatusByKey(requestSource);
    const iCalUid = requestSource.iCalUid ?? requestSource.iCalUId ?? this.filterUpdate.iCalUid ?? '';
    const bodyContent =
      typeof requestSource.body === 'string' ? requestSource.body : (requestSource.body?.content ?? '');
    const start = requestSource.start;
    const end = requestSource.end;

    return {
      id: requestSource.id ?? this.currentEventId ?? '',
      iCalUid,
      iCalUId: iCalUid,
      showAs: requestSource.showAs ?? 'busy',
      subject: requestSource.subject ?? '',
      start: this.createEventInfoDate(snapshot.start, start),
      end: this.createEventInfoDate(snapshot.end, end),
      isAllDay: !!requestSource.isAllDay,
      isCancelled: !!(requestSource.cancelled ?? requestSource.isCancelled),
      location: locations[0] ?? this.createEmptyEventInfoLocation(),
      locations,
      attendees: snapshot.recipients.map((recipient) => this.createEventInfoAttendee(recipient, attendeeStatusByKey)),
      body: {
        contentType: 'html',
        content: bodyContent,
      },
      originalStartTimeZone: requestSource.originalStartTimeZone ?? start?.timeZone ?? 'UTC',
      originalEndTimeZone: requestSource.originalEndTimeZone ?? end?.timeZone ?? 'UTC',
      onlineMeetingProvider:
        requestSource.onlineMeetingProvider ?? (requestSource.teams ? 'teamsForBusiness' : 'unknown'),
      onlineMeeting:
        requestSource.onlineMeeting ?? (requestSource.teamsUrl ? { joinUrl: requestSource.teamsUrl } : null),
    };
  }

  private createEventInfoDate(date: Date | null, fallback: IReservationDetail['start'] | undefined) {
    const timeZone = fallback?.timeZone ?? 'UTC';

    return {
      dateTime: date
        ? this.formatEventInfoDate(date, timeZone)
        : (fallback?.dateTime ?? this.formatEventInfoDate(new Date(), timeZone)),
      timeZone,
    };
  }

  private formatEventInfoDate(date: Date, timeZone: string): string {
    const useUtc = timeZone.toUpperCase() === 'UTC';
    const year = useUtc ? date.getUTCFullYear() : date.getFullYear();
    const month = (useUtc ? date.getUTCMonth() : date.getMonth()) + 1;
    const day = useUtc ? date.getUTCDate() : date.getDate();
    const hours = useUtc ? date.getUTCHours() : date.getHours();
    const minutes = useUtc ? date.getUTCMinutes() : date.getMinutes();
    const seconds = useUtc ? date.getUTCSeconds() : date.getSeconds();

    return `${this.padDatePart(year, 4)}-${this.padDatePart(month)}-${this.padDatePart(day)}T${this.padDatePart(hours)}:${this.padDatePart(minutes)}:${this.padDatePart(seconds)}`;
  }

  private padDatePart(value: number, length = 2): string {
    return value.toString().padStart(length, '0');
  }

  private createEventInfoLocation(location: LocationDetails) {
    const locationId = location.locationIdentifier?.id ?? location.emailAddress ?? location.displayName ?? '';
    const locationType = String(location.locationIdentifier?.type ?? '');

    return {
      displayName: location.displayName ?? '',
      locationUri: location.emailAddress ?? locationId,
      locationType,
      uniqueId: locationId,
      uniqueIdType: locationType,
    };
  }

  private createEmptyEventInfoLocation() {
    return {
      displayName: '',
      locationUri: '',
      locationType: '',
      uniqueId: '',
      uniqueIdType: '',
    };
  }

  private createEventInfoAttendee(recipient: EmailUser, attendeeStatusByKey: Map<string, EventInfoAttendeeStatus>) {
    const address = recipient.emailAddress ?? '';
    const name = recipient.displayName ?? address;

    return {
      type: 'required',
      status:
        this.getEventInfoAttendeeStatus([address], attendeeStatusByKey) ??
        this.getEventInfoAttendeeStatus([name], attendeeStatusByKey) ??
        this.createDefaultEventInfoAttendeeStatus(),
      emailAddress: {
        name,
        address,
      },
    };
  }

  private createEventInfoAttendeeStatusByKey(requestSource: any): Map<string, EventInfoAttendeeStatus> {
    const attendeeStatusByKey = new Map<string, EventInfoAttendeeStatus>();
    const addStatus = (keys: unknown[], value: unknown) => {
      const status = this.createEventInfoAttendeeStatus(value);
      if (!status) {
        return;
      }

      this.getUniqueNormalizedAttendeeStatusKeys(keys).forEach((key) => attendeeStatusByKey.set(key, status));
    };

    (requestSource.attendees ?? []).forEach((attendee: any) => {
      addStatus(
        [attendee.emailAddress?.address, attendee.emailAddress, attendee.displayName],
        attendee.status ?? attendee.responseStatus
      );
    });

    (requestSource.resources ?? []).forEach((resource: IEventInfoResourceResponseBm) => {
      addStatus(
        [resource.email, resource.username, resource.displayName, resource.resourceDisplayNameForApp],
        resource.responseStatus
      );
    });

    return attendeeStatusByKey;
  }

  private createEventInfoAttendeeStatus(value: unknown): EventInfoAttendeeStatus | null {
    if (typeof value === 'string') {
      return {
        response: value,
        time: this.createDefaultEventInfoAttendeeStatus().time,
      };
    }

    if (!value || typeof value !== 'object') {
      return null;
    }

    const status = value as { response?: unknown; time?: unknown };
    if (typeof status.response !== 'string' || !status.response) {
      return null;
    }

    return {
      response: status.response,
      time: this.createEventInfoAttendeeStatusTime(status.time),
    };
  }

  private getEventInfoAttendeeStatus(
    keys: unknown[],
    attendeeStatusByKey: Map<string, EventInfoAttendeeStatus>
  ): EventInfoAttendeeStatus | null {
    for (const key of this.getUniqueNormalizedAttendeeStatusKeys(keys)) {
      const status = attendeeStatusByKey.get(key);
      if (status) {
        return status;
      }
    }

    return null;
  }

  private getUniqueNormalizedAttendeeStatusKeys(keys: unknown[]): string[] {
    return this.getUniqueNormalizedValues(keys.filter((key): key is string => typeof key === 'string'));
  }

  private createDefaultEventInfoAttendeeStatus(): EventInfoAttendeeStatus {
    return {
      response: 'none',
      time: '1970-01-01T00:00:00.000Z',
    };
  }

  private createEventInfoAttendeeStatusTime(value: unknown): string {
    if (value instanceof Date) {
      return value.toISOString();
    }

    return typeof value === 'string' && value ? value : this.createDefaultEventInfoAttendeeStatus().time;
  }

  private syncSelectedResourcesFromOutlookLocations(locations: LocationDetails[], validateAddedResources = false) {
    const previousLocations = this.selectedOutlookLocations();
    const previousSelectedResourceIds = this.selectedResourceIdList();
    const removedResourceEmails = this.getRemovedOutlookLocationResourceEmails(previousLocations, locations);
    const addedLocationKeys =
      validateAddedResources && this.outlookStateInitialized
        ? this.getAddedOutlookLocationKeys(previousLocations, locations)
        : [];
    const addedResourceIds =
      validateAddedResources && this.outlookStateInitialized
        ? this.getAddedOutlookLocationResourceIds(previousLocations, locations)
        : [];

    this.selectedOutlookLocations.set(locations);
    if (removedResourceEmails.length > 0) {
      this.removeRecipientEmailsFromCurrentOutlookState(removedResourceEmails);
    }

    this.syncSelectedResourcesFromCurrentOutlookState();
    this.hideRemovedReservationDetailResources(previousSelectedResourceIds);
    let resourceFilterReloaded = false;
    if (addedLocationKeys.length > 0) {
      this.validateSelectedResourcesAfterResourceListReload = true;
      resourceFilterReloaded = this.syncCurrentEventResourceEmailsFromSelectedResources();
      if (!resourceFilterReloaded) {
        this.validateSelectedResourcesAfterResourceListReload = false;
      }
    }

    if (removedResourceEmails.length > 0) {
      this.removeResourceRecipientsFromOutlookEvent(removedResourceEmails, () =>
        this.refreshSelectedRecipientsFromOutlook()
      );
    }

    if (resourceFilterReloaded) {
      return;
    }

    this.validateSelectedResourcesAgainstCurrentList(
      addedResourceIds.length > 0 ? addedResourceIds : addedLocationKeys.length > 0 ? undefined : []
    );
  }

  private syncSelectedResourcesFromOutlookRecipients(recipientEmails: string[], validateAddedResources = false) {
    const previousRecipientEmails = this.selectedOutlookRecipientEmails();
    const previousSelectedResourceIds = this.selectedResourceIdList();
    const addedRecipientKeys =
      validateAddedResources && this.outlookStateInitialized
        ? this.getAddedOutlookRecipientKeys(previousRecipientEmails, recipientEmails)
        : [];
    const addedResourceIds =
      validateAddedResources && this.outlookStateInitialized
        ? this.getAddedOutlookRecipientResourceIds(previousRecipientEmails, recipientEmails)
        : [];

    this.selectedOutlookRecipientEmails.set(recipientEmails);
    this.syncSelectedResourcesFromCurrentOutlookState();
    this.hideRemovedReservationDetailResources(previousSelectedResourceIds);
    if (addedRecipientKeys.length > 0) {
      this.validateSelectedResourcesAfterResourceListReload = true;
      if (this.syncCurrentEventResourceEmailsFromSelectedResources()) {
        return;
      }
      this.validateSelectedResourcesAfterResourceListReload = false;
    }
    this.validateSelectedResourcesAgainstCurrentList(
      addedResourceIds.length > 0 ? addedResourceIds : addedRecipientKeys.length > 0 ? undefined : []
    );
  }

  private syncSelectedResourcesFromCurrentOutlookState() {
    const selectedResourceIds = new Set<number>([
      ...this.getSelectedResourceIdsFromOutlookLocations(this.selectedOutlookLocations()),
      ...this.getSelectedResourceIdsFromOutlookRecipientEmails(this.selectedOutlookRecipientEmails()),
    ]);

    this.selectedResourceIdList.set(Array.from(selectedResourceIds));
  }

  private hideRemovedReservationDetailResources(previousResourceIds: number[]) {
    const currentResourceIdSet = new Set(this.selectedResourceIdList());
    const removedResourceIds = previousResourceIds.filter((resourceId) => !currentResourceIdSet.has(resourceId));

    if (removedResourceIds.length > 0) {
      this.store.dispatch(
        ReservationActions.hideReservationDetailResources({
          resourceIds: removedResourceIds,
        })
      );
    }
  }

  private getSelectedResourceIdsFromOutlookLocations(locations: LocationDetails[]): number[] {
    const selectedResourceIds = new Set<number>();
    const resourceList = this.resourceList()?.data ?? [];

    locations.forEach((location) => {
      const primaryLocationKeySet = new Set(this.getLocationPrimaryMatchKeys(location));
      const primaryMatches = resourceList.filter((resource) =>
        this.getResourcePrimaryMatchKeys(resource).some((key) => primaryLocationKeySet.has(key))
      );

      if (primaryMatches.length > 0) {
        primaryMatches.forEach((resource) => selectedResourceIds.add(resource.resourceId));
        return;
      }

      const displayNameKey = this.normalizeLocationKey(location.displayName);
      if (!displayNameKey) {
        return;
      }

      if (displayNameKey.includes('@')) {
        resourceList
          .filter((resource) => this.getResourcePrimaryMatchKeys(resource).includes(displayNameKey))
          .forEach((resource) => selectedResourceIds.add(resource.resourceId));
        return;
      }

      const displayNameMatches = resourceList.filter((resource) =>
        this.getResourceDisplayMatchKeys(resource).includes(displayNameKey)
      );

      if (displayNameMatches.length === 1) {
        selectedResourceIds.add(displayNameMatches[0].resourceId);
      }
    });

    return Array.from(selectedResourceIds);
  }

  private getOutlookResourceEmails(
    locations: LocationDetails[] = this.selectedOutlookLocations(),
    recipientEmails: string[] = this.selectedOutlookRecipientEmails()
  ): string[] {
    return this.getUniqueNormalizedValues([
      ...recipientEmails,
      ...locations.flatMap((location) => this.getLocationPrimaryMatchKeys(location)),
    ]);
  }

  private syncCurrentEventResourceEmailsFromSelectedResources(): boolean {
    const resourceEmails = this.getOutlookResourceEmailsFromSelectedResources();
    const currentEmails = this.filterUpdate.currentEventResourceEmails ?? [];

    if (this.hasSameNormalizedValues(currentEmails, resourceEmails)) {
      return false;
    }

    const start = this.filterUpdate.start ?? this.eventStart();
    const end = this.filterUpdate.end ?? this.eventEnd();
    if (!start || !end) {
      return false;
    }

    this.updateResourceFilter(start, end, this.filterUpdate.iCalUid, resourceEmails, true);
    return true;
  }

  private getOutlookResourceEmailsFromSelectedResources(): string[] {
    const selectedResourceEmails = this.selectedResourceIdList()
      .map((resourceId) => this.getResourceById(resourceId)?.resourceUserName)
      .filter((resourceEmail): resourceEmail is string => !!resourceEmail);

    return this.getUniqueNormalizedValues([...this.getOutlookResourceEmails(), ...selectedResourceEmails]);
  }

  private hasSameNormalizedValues(first: string[], second: string[]): boolean {
    const firstValues = this.getNormalizedValueSet(first);
    const secondValues = this.getNormalizedValueSet(second);

    return firstValues.size === secondValues.size && Array.from(firstValues).every((value) => secondValues.has(value));
  }

  private getNormalizedValueSet(values: string[]): Set<string> {
    return new Set(values.map((value) => this.normalizeLocationKey(value)).filter((value) => value.length > 0));
  }

  private getRemovedOutlookLocationResourceEmails(
    previousLocations: LocationDetails[],
    currentLocations: LocationDetails[]
  ): string[] {
    if (previousLocations.length === 0) {
      return [];
    }

    const previousResourceIds = new Set(this.getSelectedResourceIdsFromOutlookLocations(previousLocations));
    const currentResourceIds = new Set(this.getSelectedResourceIdsFromOutlookLocations(currentLocations));
    const removedResourceEmails = Array.from(previousResourceIds)
      .filter((resourceId) => !currentResourceIds.has(resourceId))
      .map((resourceId) => this.getResourceById(resourceId)?.resourceUserName)
      .filter((resourceEmail): resourceEmail is string => !!resourceEmail);

    const currentLocationPrimaryKeys = new Set(
      currentLocations.flatMap((location) => this.getLocationPrimaryMatchKeys(location))
    );
    const removedLocationEmails = previousLocations
      .filter((location) => {
        const previousLocationPrimaryKeys = this.getLocationPrimaryMatchKeys(location);
        return (
          previousLocationPrimaryKeys.length > 0 &&
          !previousLocationPrimaryKeys.some((key) => currentLocationPrimaryKeys.has(key))
        );
      })
      .flatMap((location) => [location.emailAddress, location.locationIdentifier?.id])
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    return this.getUniqueNormalizedValues([...removedResourceEmails, ...removedLocationEmails]);
  }

  private getAddedOutlookLocationKeys(
    previousLocations: LocationDetails[],
    currentLocations: LocationDetails[]
  ): string[] {
    const previousKeys = new Set(this.getOutlookLocationKeys(previousLocations));

    return this.getOutlookLocationKeys(currentLocations).filter((key) => !previousKeys.has(key));
  }

  private getAddedOutlookLocationResourceIds(
    previousLocations: LocationDetails[],
    currentLocations: LocationDetails[]
  ): number[] {
    const previousResourceIds = new Set(this.getSelectedResourceIdsFromOutlookLocations(previousLocations));
    const currentResourceIds = new Set(this.getSelectedResourceIdsFromOutlookLocations(currentLocations));

    return Array.from(currentResourceIds).filter((resourceId) => !previousResourceIds.has(resourceId));
  }

  private removeRecipientEmailsFromCurrentOutlookState(resourceEmails: string[]) {
    const removedEmailKeys = new Set(resourceEmails.map((resourceEmail) => this.normalizeLocationKey(resourceEmail)));

    this.selectedOutlookRecipientEmails.update((recipientEmails) =>
      recipientEmails.filter((recipientEmail) => !removedEmailKeys.has(this.normalizeLocationKey(recipientEmail)))
    );
  }

  private getSelectedResourceIdsFromOutlookRecipientEmails(recipientEmails: string[]): number[] {
    const recipientEmailKeys = new Set(recipientEmails.map((email) => this.normalizeLocationKey(email)));

    return (this.resourceList()?.data ?? [])
      .filter((resource) => this.getResourcePrimaryMatchKeys(resource).some((key) => recipientEmailKeys.has(key)))
      .map((resource) => resource.resourceId);
  }

  private getAddedOutlookRecipientResourceIds(previousRecipientEmails: string[], currentRecipientEmails: string[]) {
    const previousResourceIds = new Set(this.getSelectedResourceIdsFromOutlookRecipientEmails(previousRecipientEmails));
    const currentResourceIds = new Set(this.getSelectedResourceIdsFromOutlookRecipientEmails(currentRecipientEmails));

    return Array.from(currentResourceIds).filter((resourceId) => !previousResourceIds.has(resourceId));
  }

  private getAddedOutlookRecipientKeys(previousRecipientEmails: string[], currentRecipientEmails: string[]): string[] {
    const previousKeys = new Set(this.getOutlookRecipientKeys(previousRecipientEmails));

    return this.getOutlookRecipientKeys(currentRecipientEmails).filter((key) => !previousKeys.has(key));
  }

  private getOutlookRecipientEmails(callback: (emails: string[]) => void) {
    this.getOutlookRecipients((recipients) => {
      callback(recipients.map((recipient) => recipient.emailAddress));
    });
  }

  private getOutlookRecipients(callback: (recipients: EmailUser[]) => void) {
    const item = Office.context.mailbox.item as AppointmentCompose;
    this.getRecipientsFromList(item.requiredAttendees, (requiredAttendees) => {
      this.getRecipientsFromList(item.optionalAttendees, (optionalAttendees) => {
        callback([...requiredAttendees, ...optionalAttendees]);
      });
    });
  }

  private getRecipientsFromList(recipients: Office.Recipients, callback: (recipients: EmailUser[]) => void) {
    if (!recipients) {
      callback([]);
      return;
    }

    recipients.getAsync((result) => {
      callback(result.status === Office.AsyncResultStatus.Succeeded ? (result.value ?? []) : []);
    });
  }

  private getOutlookEventDates(item: AppointmentCompose, callback: (start: Date | null, end: Date | null) => void) {
    if (!item.start?.getAsync || !item.end?.getAsync) {
      callback(null, null);
      return;
    }

    item.start.getAsync((startResult) => {
      item.end.getAsync((endResult) => {
        const start =
          startResult.status === Office.AsyncResultStatus.Succeeded ? this.parseEventDate(startResult.value) : null;
        const end =
          endResult.status === Office.AsyncResultStatus.Succeeded ? this.parseEventDate(endResult.value) : null;

        callback(start, end);
      });
    });
  }

  private validateSelectedResourcesAgainstCurrentList(resourceIds?: number[]) {
    if (!this.shouldUseResourceAvailabilityRestrictions) {
      return;
    }

    const selectedResourceIds = new Set(this.selectedResourceIdList());
    const resourceIdsToValidate = Array.from(
      new Set(resourceIds === undefined ? this.selectedResourceIdList() : resourceIds)
    ).filter((resourceId) => selectedResourceIds.has(resourceId));

    if (resourceIdsToValidate.length === 0) {
      return;
    }

    const selectedResources = resourceIdsToValidate
      .map((resourceId) => this.getResourceById(resourceId))
      .filter((resource): resource is IResourceListItem => !!resource)
      .map((resource) => this.getResourceWithCurrentEventUnavailabilityReason(resource));
    const invalidResources = selectedResources.filter(
      (resource) =>
        this.isResourceInvalidForAutoRemoval(resource) &&
        !this.removingResourceIds.has(resource.resourceId) &&
        !this.hasConfirmedResourceRestrictionOverride(resource) &&
        !this.hasDismissedResourceRemovalConfirmation(resource)
    );

    if (invalidResources.length === 0) {
      return;
    }

    if (this.shouldAskBeforeModifyingOutlookEvent) {
      this.showAutoRemovalConfirmation(invalidResources);
      return;
    }

    if (!this.shouldAutomaticallyEnforceResourceAvailabilityRestrictions) {
      return;
    }

    this.removeInvalidResourcesFromOutlookEvent(invalidResources);
  }

  private removeInvalidResourcesFromOutlookEvent(invalidResources: IResourceListItem[]) {
    invalidResources.forEach((resource) => this.removingResourceIds.add(resource.resourceId));
    this.removeResourcesFromOutlookEvent(invalidResources, (removedResources) => {
      invalidResources.forEach((resource) => this.removingResourceIds.delete(resource.resourceId));

      if (removedResources.length > 0) {
        this.store.dispatch(
          ReservationActions.hideReservationDetailResources({
            resourceIds: removedResources.map((resource) => resource.resourceId),
          })
        );
        this.showAutoRemovalNotification(removedResources);
      }

      this.refreshSelectedOutlookResources();
    });
  }

  private removeResourcesFromOutlookEvent(
    resources: IResourceListItem[],
    callback: (removedResources: IResourceListItem[]) => void,
    removedResources: IResourceListItem[] = []
  ) {
    const [resource, ...remainingResources] = resources;
    if (!resource) {
      callback(removedResources);
      return;
    }

    this.removeResourceFromOutlookEvent(resource.resourceUserName, (success) => {
      this.removeResourcesFromOutlookEvent(
        remainingResources,
        callback,
        success ? [...removedResources, resource] : removedResources
      );
    });
  }

  private removeResourceRecipientsFromOutlookEvent(resourceEmails: string[], callback: () => void) {
    const [resourceEmail, ...remainingResourceEmails] = resourceEmails;
    if (!resourceEmail) {
      callback();
      return;
    }

    this.removeResourceRecipient(resourceEmail, () => {
      this.removeResourceRecipientsFromOutlookEvent(remainingResourceEmails, callback);
    });
  }

  private showAutoRemovalNotification(resources: IResourceListItem[]) {
    const notificationId = ++this.notificationId;
    const notification: ResourceNotification = {
      id: notificationId,
      messageKey: this.translation.outlookAddin.resourcesRemovedDueRestrictions,
      resources: resources.map((resource) => this.getResourceDisplayName(resource)).join(', '),
      resourceDetails: resources.map((resource) => ({
        displayName: this.getResourceDisplayName(resource),
        resource,
      })),
      start: this.getWarningDateValue(this.resourceFilter()?.start ?? this.eventStart()),
      end: this.getWarningDateValue(this.resourceFilter()?.end ?? this.eventEnd()),
    };

    this.resourceNotification.emit(notification);
  }

  private showAutoRemovalConfirmation(resources: IResourceListItem[]) {
    resources.forEach((resource) => this.showSingleAutoRemovalConfirmation(resource));
  }

  private showSingleAutoRemovalConfirmation(resource: IResourceListItem) {
    const notificationKey = this.getResourceRestrictionConfirmationKey([resource]);
    if (this.pendingResourceRestrictionConfirmationKeys.has(notificationKey)) {
      return;
    }

    this.pendingResourceRestrictionConfirmationKeys.add(notificationKey);
    const clearPendingConfirmation = () => {
      this.pendingResourceRestrictionConfirmationKeys.delete(notificationKey);
    };
    const notificationId = ++this.notificationId;
    const notification: ResourceNotification = {
      id: notificationId,
      messageKey: this.translation.outlookAddin.resourcesShouldBeRemovedDueRestrictions,
      resources: this.getResourceDisplayName(resource),
      resourceDetails: [
        {
          displayName: this.getResourceDisplayName(resource),
          resource,
        },
      ],
      start: this.getWarningDateValue(this.resourceFilter()?.start ?? this.eventStart()),
      end: this.getWarningDateValue(this.resourceFilter()?.end ?? this.eventEnd()),
      confirmation: {
        primaryAction: () => {
          clearPendingConfirmation();
          if (this.getResourceRestrictionConfirmationKey([resource]) !== notificationKey) {
            return;
          }

          const currentResource = this.getResourceById(resource.resourceId);
          if (!currentResource) {
            return;
          }

          const currentResourceForValidation = this.getResourceWithCurrentEventUnavailabilityReason(currentResource);
          if (!this.isResourceInvalidForAutoRemoval(currentResourceForValidation)) {
            return;
          }

          this.removeInvalidResourcesFromOutlookEvent([currentResourceForValidation]);
        },
        secondaryAction: () => {
          clearPendingConfirmation();
          if (this.getResourceRestrictionConfirmationKey([resource]) === notificationKey) {
            this.addDismissedResourceRemovalConfirmation(resource);
          }
        },
        timeoutMs: this.resourceRestrictionConfirmationTimeoutMs,
      },
    };

    this.resourceNotification.emit(notification);
  }

  private getResourceRestrictionConfirmationKey(resources: IResourceListItem[]): string {
    const resourceIds = resources
      .map((resource) => resource.resourceId)
      .sort((a, b) => a - b)
      .join(',');

    return [
      resourceIds,
      this.getWarningDateKey(this.resourceFilter()?.start ?? this.eventStart()),
      this.getWarningDateKey(this.resourceFilter()?.end ?? this.eventEnd()),
    ].join('|');
  }

  private getWarningDateKey(value: unknown): string {
    const date = this.getWarningDateValue(value);

    return Number.isFinite(date.getTime()) ? date.toISOString() : '';
  }

  private addConfirmedResourceRestrictionOverride(resource: IResourceListItem) {
    this.confirmedResourceRestrictionOverrideKeys.add(this.getResourceRestrictionOverrideKey(resource));
  }

  private removeConfirmedResourceRestrictionOverride(resource: IResourceListItem) {
    this.confirmedResourceRestrictionOverrideKeys.delete(this.getResourceRestrictionOverrideKey(resource));
  }

  private hasConfirmedResourceRestrictionOverride(resource: IResourceListItem): boolean {
    return this.confirmedResourceRestrictionOverrideKeys.has(this.getResourceRestrictionOverrideKey(resource));
  }

  private addDismissedResourceRemovalConfirmation(resource: IResourceListItem) {
    this.store.dispatch(ReservationActions.dismissResourceRemovalConfirmation({ resourceId: resource.resourceId }));
  }

  private hasDismissedResourceRemovalConfirmation(resource: IResourceListItem): boolean {
    return this.dismissedResourceRemovalConfirmationResourceIds.has(resource.resourceId);
  }

  private clearDismissedResourceRemovalConfirmations() {
    this.store.dispatch(ReservationActions.clearDismissedResourceRemovalConfirmations());
  }

  private getResourceRestrictionOverrideKey(resource: IResourceListItem): string {
    return [
      resource.resourceId,
      this.getWarningDateKey(this.resourceFilter()?.start ?? this.eventStart()),
      this.getWarningDateKey(this.resourceFilter()?.end ?? this.eventEnd()),
    ].join('|');
  }

  private validateSelectedResourcesAgainstPastEventEnd(): boolean {
    const invalidResources = this.selectedResourceIdList()
      .map((resourceId) => this.getResourceById(resourceId))
      .filter((resource): resource is IResourceListItem => !!resource)
      .filter((resource) => !this.removingResourceIds.has(resource.resourceId))
      .map((resource) => this.markResourceAsPastEventEnd(resource));
    const resourcesToValidate = invalidResources.filter(
      (resource) => !this.hasDismissedResourceRemovalConfirmation(resource)
    );

    if (resourcesToValidate.length === 0) {
      return false;
    }

    if (this.shouldAskBeforeModifyingOutlookEvent) {
      this.showAutoRemovalConfirmation(resourcesToValidate);
    } else if (this.shouldAutomaticallyEnforceResourceAvailabilityRestrictions) {
      this.removeInvalidResourcesFromOutlookEvent(resourcesToValidate);
    }

    return true;
  }

  private isResourceInvalidForAutoRemoval(resource: IResourceListItem): boolean {
    return !this.isResourceAvailable(resource) && !this.shouldPreserveReservedResourceWithUnknownCurrentEvent(resource);
  }

  private shouldPreserveReservedResourceWithUnknownCurrentEvent(resource: IResourceListItem): boolean {
    return (
      !!this.currentEventItemId &&
      !this.filterUpdate.iCalUid &&
      !resource.unavailabilityReason &&
      resource.timeLine.indexOf(ResourceReservationStatusType.Reserved) > -1
    );
  }

  private findLocationForAttendee(
    locations: LocationDetails[],
    attendeeEmailAddress: string
  ): LocationDetails | undefined {
    const attendeeKey = this.normalizeLocationKey(attendeeEmailAddress);

    return locations.find((location) => this.getLocationPrimaryMatchKeys(location).includes(attendeeKey));
  }

  private getLocationPrimaryMatchKeys(location: LocationDetails): string[] {
    return this.getUniqueNormalizedKeys([location.emailAddress, location.locationIdentifier?.id]);
  }

  private getOutlookLocationKeys(locations: LocationDetails[]): string[] {
    return this.getUniqueNormalizedKeys(
      locations.flatMap((location) => [...this.getLocationPrimaryMatchKeys(location), location.displayName])
    );
  }

  private getOutlookRecipientKeys(recipientEmails: string[]): string[] {
    return this.getUniqueNormalizedKeys(recipientEmails);
  }

  private getResourcePrimaryMatchKeys(resource: IResourceListItem): string[] {
    return this.getUniqueNormalizedKeys([resource.resourceUserName]);
  }

  private getResourceDisplayMatchKeys(resource: IResourceListItem): string[] {
    return this.getUniqueNormalizedKeys([
      ...this.getLocalizedStringValues(resource.resourceDisplayNameForApp),
      ...this.getLocalizedStringValues(resource.resourceDisplayName),
    ]);
  }

  private getLocalizedStringValues(value: LocalizedString): unknown[] {
    if (!value) {
      return [];
    }

    return [
      localized(value, this.translocoService),
      (value as any)['ɵvalue'],
      ...(typeof value === 'object' ? Object.values(value) : [value]),
    ];
  }

  private getUniqueNormalizedKeys(values: unknown[]): string[] {
    return Array.from(
      new Set(
        values.map((value) => this.normalizeLocationKey(value)).filter((value): value is string => value.length > 0)
      )
    );
  }

  private getUniqueNormalizedValues(values: string[]): string[] {
    const normalizedValues = new Set<string>();

    return values.filter((value) => {
      const normalizedValue = this.normalizeLocationKey(value);
      if (!normalizedValue || normalizedValues.has(normalizedValue)) {
        return false;
      }

      normalizedValues.add(normalizedValue);
      return true;
    });
  }

  private normalizeLocationKey(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  onDetailRefresh() {
    this.store.dispatch(ResourcesActions.loadResourceDetail({ resourceId: this.selectedResourceDetail.resourceId }));
  }

  private getResourceListItemById(resourceId: number) {
    const selectedResourceItem = this.getResourceById(resourceId);
    if (!selectedResourceItem) {
      return null;
    }

    return {
      displayName: this.getResourceDisplayName(selectedResourceItem),
      emailAddress: selectedResourceItem.resourceUserName,
    };
  }

  private getResourceById(resourceId: number): IResourceListItem | null {
    return (this.resourceList()?.data ?? []).find((x) => x.resourceId === resourceId) ?? null;
  }

  private hasSelectedResourceWithSameRestrictedType(resource: IResourceListItem): boolean {
    if (resource.notAllowedMultipeResourceTypeAndResourceOccupied === true) {
      return true;
    }

    if (resource.resourceTypeId == null) {
      return false;
    }

    const resourceType = this.resourceFilters().find((item) => item.resourceTypeId === resource.resourceTypeId);
    if (resourceType?.allowMultipleResourceReservations !== false) {
      return false;
    }

    return this.selectedResourceIdList().some(
      (selectedResourceId) => this.getResourceById(selectedResourceId)?.resourceTypeId === resource.resourceTypeId
    );
  }

  private getResourceDisplayName(resource: IResourceListItem): string {
    return (
      localized(resource.resourceDisplayNameForApp, this.translocoService) ??
      localized(resource.resourceDisplayName, this.translocoService) ??
      resource.resourceUserName
    );
  }

  private performAppointmentAction(item: AppointmentCompose) {
    const obs0 = new Observable<LocationDetails[]>((observer) => {
      Office.context.mailbox.item.enhancedLocation.getAsync((x) => {
        observer.next(x.value);
        observer.complete();
      });
    });

    const obs1 = new Observable<any>((observer) => {
      item.start.getAsync((x) => {
        observer.next(x.value);
        observer.complete();
      });
    });

    const obs2 = new Observable<any>((observer) => {
      item.end.getAsync((x) => {
        observer.next(x.value);
        observer.complete();
      });
    });

    const obs3 = this.getCurrentEventIdentity(item);
    const obs4 = this.getOutlookRecipientEmails$();

    return forkJoin([obs1, obs2, obs0, obs3, obs4]).subscribe(
      ([start, end, resourceList, currentEventIdentity, recipientEmails]) => {
        this.currentEventStart = currentEventIdentity.start ?? new Date(start);
        this.currentEventEnd = currentEventIdentity.end ?? new Date(end);
        this.currentEventId = currentEventIdentity.eventId;
        this.currentEventStartLocalDateTime = currentEventIdentity.startLocalDateTime;
        this.currentEventEndLocalDateTime = currentEventIdentity.endLocalDateTime;
        this.currentEventStartTimeZone = currentEventIdentity.startTimeZone;
        this.currentEventEndTimeZone = currentEventIdentity.endTimeZone;
        this.updateResourceFilter(
          new Date(start).toISOString(),
          new Date(end).toISOString(),
          currentEventIdentity.iCalUid,
          this.getOutlookResourceEmails(resourceList ?? [], recipientEmails ?? [])
        );
        this.syncSelectedResourcesFromOutlookLocations(resourceList ?? []);
        this.syncSelectedResourcesFromOutlookRecipients(recipientEmails ?? []);
        this.outlookStateInitialized = true;
      }
    );
  }

  private getCurrentEventIdentity(item: AppointmentCompose): Observable<CurrentEventIdentity> {
    return this.getCurrentEventItemId(item).pipe(
      switchMap((itemId) => {
        this.currentEventItemId = itemId;
        if (!itemId) {
          return of(this.createEmptyCurrentEventIdentity());
        }

        const restItemId = this.getRestItemId(itemId);
        return this.touchOneHttpService.getICalUid(restItemId).pipe(
          map((event) => ({
            iCalUid: event?.iCalUId ?? event?.iCalUid ?? null,
            eventId: event?.id ?? restItemId,
            start: this.parseGraphEventDate(event?.start),
            end: this.parseGraphEventDate(event?.end),
            startLocalDateTime: this.getGraphEventDateTime(event?.start),
            endLocalDateTime: this.getGraphEventDateTime(event?.end),
            startTimeZone: event?.start?.timeZone ?? null,
            endTimeZone: event?.end?.timeZone ?? null,
          })),
          catchError(() =>
            of({
              ...this.createEmptyCurrentEventIdentity(),
              eventId: restItemId,
            })
          )
        );
      })
    );
  }

  private createEmptyCurrentEventIdentity(): CurrentEventIdentity {
    return {
      iCalUid: null,
      eventId: null,
      start: null,
      end: null,
      startLocalDateTime: null,
      endLocalDateTime: null,
      startTimeZone: null,
      endTimeZone: null,
    };
  }

  private getCurrentEventItemId(item: AppointmentCompose): Observable<string | null> {
    const itemId = (item as AppointmentCompose & { itemId?: string }).itemId;
    if (itemId) {
      return of(itemId);
    }

    const itemWithAsyncId = item as AppointmentCompose & {
      getItemIdAsync?: (callback: (result: Office.AsyncResult<string>) => void) => void;
    };

    if (typeof itemWithAsyncId.getItemIdAsync !== 'function') {
      return of(null);
    }

    return new Observable<string | null>((observer) => {
      itemWithAsyncId.getItemIdAsync((result) => {
        observer.next(result.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
        observer.complete();
      });
    });
  }

  private getRestItemId(itemId: string): string {
    try {
      return Office.context.mailbox.convertToRestId(itemId, 'v2.0');
    } catch {
      return itemId;
    }
  }

  private parseGraphEventDate(value: { dateTime?: Date | string; timeZone?: string } | undefined): Date | null {
    if (!value?.dateTime) {
      return null;
    }

    if (
      typeof value.dateTime === 'string' &&
      value.timeZone?.toUpperCase() === 'UTC' &&
      !/[zZ]|[+-]\d{2}:\d{2}$/.test(value.dateTime)
    ) {
      return this.parseEventDate(`${value.dateTime}Z`);
    }

    return this.parseEventDate(value.dateTime);
  }

  private getGraphEventDateTime(value: { dateTime?: Date | string } | undefined): string | null {
    if (!value?.dateTime) {
      return null;
    }

    return value.dateTime instanceof Date ? value.dateTime.toISOString() : value.dateTime;
  }

  private parseEventDate(value: Date | string): Date | null {
    const date = value instanceof Date ? value : new Date(value);

    return Number.isFinite(date.getTime()) ? date : null;
  }

  private getOutlookRecipientEmails$(): Observable<string[]> {
    return new Observable<string[]>((observer) => {
      this.getOutlookRecipientEmails((emails) => {
        observer.next(emails);
        observer.complete();
      });
    });
  }

  expandShrinkGroup(itemIndex: number) {
    const itemIxdInList = this.shrinkItemIndexList.indexOf(itemIndex);
    if (itemIxdInList > -1) {
      this.shrinkItemIndexList.splice(itemIxdInList, 1);
    } else {
      this.shrinkItemIndexList.push(itemIndex);
    }
  }

  isItemShrinked(itemIndex: number) {
    return this.shrinkItemIndexList.indexOf(itemIndex) > -1;
  }

  isResourceAvailable(item: IResourceListItem): boolean {
    if (this.isCurrentEventEndInPast() || this.isResourceUnavailableByResourceType(item)) {
      return false;
    }

    return (
      item.isAvailable ||
      this.isReservedInCurrentEvent(item) ||
      (!item.unavailabilityReason &&
        item.availableOutsideWorkingHours &&
        item.timeLine.indexOf(ResourceReservationStatusType.Reserved) == -1)
    );
  }

  isResourceUnavailableByResourceType(item: IResourceListItem): boolean {
    return !this.isResourceSelected(item.resourceId) && this.hasSelectedResourceWithSameRestrictedType(item);
  }

  isCurrentEventResourceAvailable(item: IResourceListItem): boolean {
    return this.isResourceSelected(item.resourceId) && this.isResourceAvailable(item);
  }

  private isReservedInCurrentEvent(item: IResourceListItem): boolean {
    return !!item.inCurrentEvent && !item.unavailabilityReason && this.hasReservedStatus(item);
  }

  private hasReservedStatus(item: IResourceListItem): boolean {
    return (
      item.unavailabilityReason === ResourceUnavailabilityReason.Reserved ||
      (!item.unavailabilityReason && item.timeLine.indexOf(ResourceReservationStatusType.Reserved) > -1)
    );
  }

  private getResourceWithCurrentEventUnavailabilityReason(resource: IResourceListItem): IResourceListItem {
    return this.isCurrentEventEndInPast() ? this.markResourceAsPastEventEnd(resource) : resource;
  }

  private markResourceAsPastEventEnd(resource: IResourceListItem): IResourceListItem {
    return {
      ...resource,
      isAvailable: false,
      availableOutsideWorkingHours: false,
      unavailabilityReason: ResourceUnavailabilityReason.EventEndsInPast,
      unavailabilityReasonValue: null,
    };
  }

  private isCurrentEventEndInPast(): boolean {
    return this.isEventEndInPast(this.filterUpdate.end ?? this.eventEnd());
  }

  private isEventEndInPast(value: unknown): boolean {
    if (!value) {
      return false;
    }

    const time = new Date(value as string).getTime();

    return Number.isFinite(time) && time <= Date.now();
  }

  shrinkItemIndexList: number[] = [];
}
